// "What the music is doing at time t" for programs (shared/program.ts), from a
// song's saved analysis. Used by each Pi and by the light board's preview, so
// both render the same thing.
//
// Beat position comes from the song's tracked beats (so effects follow tempo
// drift). Levels are smoothed: they rise at once but fall over about 0.3 s,
// so lights swell with the music instead of flickering on every frame.

import type { SavedAnalysis } from "./library.ts";
import type { MusicMoment } from "./program.ts";
import { decodeSong, featuresAt, type DecodedSong } from "./song.ts";

export interface SongClock {
  song: DecodedSong;
}

export function songClock(analysis: SavedAnalysis): SongClock {
  return { song: decodeSong(analysis) };
}

// Fall: each step back in time counts 15% less (7 steps of 50 ms ≈ 0.3 s).
const SMOOTH_STEPS = 7;
const SMOOTH_STEP_S = 0.05;
const SMOOTH_DECAY = 0.85;

export function momentAt(clock: SongClock, t: number): MusicMoment {
  const now = featuresAt(clock.song, t);
  let loudness = now.loudness;
  let bass = now.bass;
  let treble = now.treble;
  let weight = 1;
  for (let k = 1; k < SMOOTH_STEPS; k++) {
    weight *= SMOOTH_DECAY;
    const past = featuresAt(clock.song, t - k * SMOOTH_STEP_S);
    loudness = Math.max(loudness, past.loudness * weight);
    bass = Math.max(bass, past.bass * weight);
    treble = Math.max(treble, past.treble * weight);
  }
  // Before the first beat, count beats back from it at the song's tempo.
  const beatSeconds = Math.max(0.1, now.beatSeconds || 60 / Math.max(20, clock.song.bpm));
  return {
    time: t,
    beatIndex: now.beatIndex,
    beatPhase: Math.min(Math.max(now.beatPhase, 0), 0.999),
    beatSeconds,
    loudness,
    bass,
    treble,
    pitchClass: now.pitchClass,
    pitchConfidence: now.pitchConfidence,
  };
}
